import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Container, Fade, Paper, Stack } from "@mui/material";
import { Link, useParams } from "react-router-dom";

import { PageTitle, Logo } from "@components/common";
import { CircularProgress, Typography } from "@components/base";
import {
  ValidateProcurementSupplierForm,
  ValidateProcurementSupplierFormValues,
} from "@components/form";
import { useGetCompanyName } from "@api";
import { useRegisterSupplierStore } from "@store/registerSupplierStore";
import RegisterProcurementSupplierForm from "@components/form/RegisterProcurementSupplierForm";

const RegisterSupplierPage: FC = () => {
  const { t } = useTranslation();

  const [isValidated, setIsValidated] = useState(false);
  const { companyId } = useParams();

  const { validatedData, setValidatedData } = useRegisterSupplierStore();

  if (!companyId) {
    // TODO: add fallback page - invalid link -> <Navigate to="/404" />
    return;
  }

  const { company, isPendingCompanyDetail, isSucessGetCompanyDetail } =
    useGetCompanyName({
      companyId,
    });

  const handleValidationSuccess = (
    data: ValidateProcurementSupplierFormValues,
  ): void => {
    setValidatedData(data);
    setIsValidated(true);
  };

  const handleGoBack = (): void => {
    setIsValidated(false);
  };

  const label = !isValidated
    ? `${t(`csob.supplier.becameSupplierOf`)} ${company?.companyName}`
    : t("csob.supplier.enterUserData");

  return (
    <Box component="main" pt={6}>
      <PageTitle
        title={`${t("procurement.register.supplier.pageTitle")} | Green0Meter`}
      />
      <Container maxWidth="sm">
        {isPendingCompanyDetail ? (
          <Stack alignItems="center" justifyContent="center">
            <CircularProgress />
          </Stack>
        ) : (
          <Fade in={isSucessGetCompanyDetail} timeout={500}>
            <Paper>
              <Stack direction="column" p={4} rowGap={3}>
                <Stack alignItems="center" direction="column" rowGap={2}>
                  <Link to="/login">
                    <Logo
                      height={75}
                      width={120}
                      type="green0meter-with-blue-text"
                    />
                  </Link>
                  <Typography variant="body1">
                    {!isValidated
                      ? t("procurement.register.supplier.step1")
                      : t("procurement.register.supplier.step2")}
                  </Typography>

                  <Typography fontWeight={500} variant="h5" textAlign={"center"}>
                    {label}
                  </Typography>
                </Stack>

                {isValidated && validatedData ? (
                  <RegisterProcurementSupplierForm handleGoBack={handleGoBack} />
                ) : (
                  <ValidateProcurementSupplierForm
                    companyId={companyId}
                    onSuccess={handleValidationSuccess}
                  />
                )}
              </Stack>
            </Paper>
          </Fade>
        )}
      </Container>
    </Box>
  );
};

export default RegisterSupplierPage;
